/**
 * Corradino Ottanta — shared storefront behaviour.
 * Small, dependency-free enhancements around native elements:
 * the mobile <details> menu, the colour concept carousel, the
 * scroll reveal and the native Shopify signup form.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReducedMotion() {
    return reduceMotion.matches;
  }

  /* Mobile drawer: keep native <details> behaviour, close after choosing a link. */
  function setupDrawers() {
    document.querySelectorAll('[data-co-drawer]').forEach(function (drawer) {
      drawer.addEventListener('click', function (event) {
        if (event.target.closest('a')) drawer.removeAttribute('open');
      });

      drawer.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape' || !drawer.hasAttribute('open')) return;
        drawer.removeAttribute('open');
        var summary = drawer.querySelector('summary');
        if (summary) summary.focus();
      });
    });
  }

  /* Colour concept carousel: arrows, dots and current-slide state. */
  function setupCarousels() {
    document.querySelectorAll('[data-co-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('[data-co-carousel-track]');
      if (!track) return;

      var dots = Array.prototype.slice.call(carousel.querySelectorAll('[data-co-carousel-dot]'));
      var items = Array.prototype.slice.call(track.querySelectorAll('.co-carousel__item'));
      var prev = carousel.querySelector('[data-co-carousel-prev]');
      var next = carousel.querySelector('[data-co-carousel-next]');

      function stepSize() {
        var first = items[0];
        if (!first) return track.clientWidth * 0.8;
        var gap = parseFloat(window.getComputedStyle(track).columnGap || '20') || 20;
        return first.getBoundingClientRect().width + gap;
      }

      function scrollToIndex(index) {
        var item = items[index];
        if (!item) return;
        track.scrollTo({
          left: item.offsetLeft - track.offsetLeft,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }

      function update() {
        var max = track.scrollWidth - track.clientWidth;
        var progress = max > 0 ? track.scrollLeft / max : 0;
        var index = Math.round(progress * Math.max(items.length - 1, 0));

        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });

        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      }

      function move(direction) {
        track.scrollBy({
          left: direction * stepSize(),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }

      if (prev) {
        prev.addEventListener('click', function () {
          move(-1);
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          move(1);
        });
      }

      dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          scrollToIndex(index);
        });
      });

      track.addEventListener('scroll', function () {
        window.requestAnimationFrame(update);
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* Quiet scroll reveal, skipped entirely for reduced motion. */
  function setupReveals() {
    var targets = document.querySelectorAll('[data-co-reveal]');
    if (!targets.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach(function (target) {
        target.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    targets.forEach(function (target) {
      observer.observe(target);
    });
  }

  /* Signup: surface Shopify's own success/error state to keyboard users. */
  function setupSignup() {
    document.querySelectorAll('[data-co-signup]').forEach(function (root) {
      var message = root.querySelector('[data-co-signup-message]');
      if (message) message.setAttribute('tabindex', '-1');

      var invalidInput = root.querySelector('input[type="email"][aria-invalid="true"]');
      if (invalidInput) {
        var input = invalidInput;
        if (input) input.focus();
      }
    });
  }

  function init() {
    setupDrawers();
    setupCarousels();
    setupReveals();
    setupSignup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
